import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { AnimatePresence, motion } from "framer-motion";
import { LucideIcon } from "lucide-react";
import React, { useMemo } from "react";
import { IconType } from "react-icons/lib";

interface SidebarLinkProps extends React.HTMLAttributes<HTMLButtonElement> {
  mode: "expanded" | "collapsed";
  icon: LucideIcon | IconType | React.ComponentType;
  ariaLabel?: string;
  label: string | React.ReactNode;
  showTooltip?: boolean;
  unreadCount?: number;
}

const SidebarLink = React.forwardRef<HTMLButtonElement, SidebarLinkProps>(
  (
    {
      mode,
      id,
      icon: Icon,
      ariaLabel,
      label,
      showTooltip = true,
      unreadCount = 0,
      className,
      ...props
    },
    ref,
  ) => {
    const expanded = mode === "expanded";

    const linkContent = useMemo(
      () => (
        <button
          {...props}
          ref={ref}
          aria-label={
            ariaLabel
              ? ariaLabel
              : typeof label === "string"
                ? label
                : undefined
          }
          className={cn(
            "flex items-center w-full rounded-full transition-all duration-300 ease-in-out cursor-pointer hover:bg-accent/50",
            "relative",
            className,
            {
              "pr-5": expanded,
            },
          )}
        >
          <div className="relative w-[40px] h-[40px] flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5" strokeWidth={1.5} />
            <AnimatePresence>
              {unreadCount > 0 && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 25 }}
                  className="absolute -top-1 left-[calc(100%-15px)] bg-red-700 text-white text-[.7rem] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <span
            className={cn(
              "whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out",
              expanded ? "opacity-100 ml-1" : "opacity-0 w-0",
            )}
          >
            {label}
          </span>
        </button>
      ),
      [props, label, expanded, className, unreadCount],
    );

    if (!expanded && showTooltip) {
      return (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
            <TooltipContent
              side="right"
              align="center"
              className="text-md font-medium z-60"
            >
              {label}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return linkContent;
  },
);

export default React.memo(SidebarLink);
